import React, { use } from 'react';

import { ClubContentAndMetaConsumer } from 'src/content/club-content-consumer';
import type { EnabledPages } from 'src/content/enabled-pages-helper';
import { SiteContext } from 'src/context/site-context';
import type { Styling } from 'src/context/styling';
import { getClubDisplayValues } from 'src/enums/build/build-type';
import { clubNameDependingOnBuildTypeAndLanguage } from 'src/helpers/club-helper';
import type { NavigationPagesToDisplay } from 'src/models/cms/navigation-page';
import mq from 'src/styling/media-queries';
import { TranslatedStaticText } from 'src/translators/static-text-translator';

import { getFacilitiesAtClub } from './facilities';
import type { ClubFacilityKeyWithVideo, FacilityDetails } from './facilities';
import Facility from './facility';

const wrapperStyle = (shouldCenter: boolean, useSmallThumbnails?: boolean) =>
  mq({
    marginBottom: useSmallThumbnails ? null : '4rem',
    width: shouldCenter ? null : '100%',
    maxWidth: shouldCenter ? [null, '70rem'] : null,
    marginLeft: shouldCenter ? [null, 'auto'] : null,
    marginRight: shouldCenter ? [null, 'auto'] : null,
  });

const titleStyle = (styling: Styling, isDL: boolean) =>
  mq([
    styling.texts.header,
    isDL ? styling.fonts.regular : {},
    {
      fontSize: ['2rem', '2.5rem'],
      lineHeight: 1,
      marginTop: '4rem',
      color: styling.colors.bodyText,
    },
  ]);

const subtitleStyle = (styling: Styling) =>
  mq([
    styling.fonts.light,
    {
      lineHeight: 1.5,
      color: styling.colors.bodyText,
      fontSize: ['1rem', '1.25rem'],
      maxWidth: [null, '50rem'],
      margin: [0, '0 auto'],
    },
  ]);

const facilityWrapperStyle = (useSmallThumbnails?: boolean) =>
  mq({
    'width': '100%',
    '*zoom': 1,
    'clear': 'both',
    'content': '" "',
    'padding': ['0 1rem', '1rem 0 0'],
    'display': useSmallThumbnails ? 'flex' : 'table',
    'justifyContent': useSmallThumbnails ? 'center' : null,
  });

type FacilitySelectProps = {
  useSmallThumbnails?: boolean;
  center?: boolean;

  facilityTypeToHide?: FacilityDetails;
  includeTitle?: boolean;
  gtmTrackingId?: string;
  onClick?: () => void;
  isNav?: boolean;
};

type FacilitySelectContentProps = FacilitySelectProps & {
  clubName: string;
  clubFacilities: ClubFacilityKeyWithVideo[] | undefined;
  enabledPages: EnabledPages;
  navigationPagesToDisplay: NavigationPagesToDisplay | undefined;
  onClick?: () => void;
};

const FacilitySelect = (props: FacilitySelectProps) => (
  <ClubContentAndMetaConsumer>
    {(cmsData, clubMetadata) => (
      <FacilitySelectContent
        {...props}
        clubName={clubMetadata.clubName}
        clubFacilities={cmsData.sharedContent.clubFacilities}
        enabledPages={cmsData.pages}
        navigationPagesToDisplay={cmsData.navigationMenuContent.navigationPagesToDisplay}
      />
    )}
  </ClubContentAndMetaConsumer>
);

const FacilitySelectContent = ({
  clubFacilities,
  facilityTypeToHide,
  includeTitle,
  useSmallThumbnails,
  center,
  gtmTrackingId,
  clubName,
  enabledPages,
  navigationPagesToDisplay,
  onClick,
  isNav,
}: FacilitySelectContentProps) => {
  const { styling, buildType, isDL, language } = use(SiteContext);
  const isCenterFalse = center === false;

  const facilitiesToShow = getFacilitiesAtClub(
    clubFacilities,
    enabledPages,
    navigationPagesToDisplay?.clubFacilitiesPages,
    facilityTypeToHide
  );

  if (facilitiesToShow.length === 0) {
    return null;
  }

  return (
    <div css={wrapperStyle(!isCenterFalse, useSmallThumbnails)}>
      {includeTitle ? (
        <div css={{ textAlign: 'center' }}>
          <h2 css={titleStyle(styling, isDL)}>
            <TranslatedStaticText lookupKey="clubFacilities" />
          </h2>
          <div css={subtitleStyle(styling)}>
            <TranslatedStaticText
              lookupKey="highlightsAtClub"
              params={{
                clubPrefix: getClubDisplayValues(buildType).clubNamePrefix,
                clubName: clubNameDependingOnBuildTypeAndLanguage(clubName, buildType, language),
              }}
            />
          </div>
        </div>
      ) : null}

      <div css={facilityWrapperStyle(useSmallThumbnails)}>
        {facilitiesToShow.map((facility) => (
          <Facility
            key={facility.urlPath}
            facilityType={facility}
            useSmallThumbnails={!!useSmallThumbnails}
            gtmTrackingId={gtmTrackingId}
            onClick={onClick}
            isNav={isNav}
          />
        ))}
      </div>
    </div>
  );
};

export default FacilitySelect;
